import React, { useCallback, useMemo, useState } from "react";
import { Download, FileSpreadsheet, Search, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";

const SOURCES = [
  { key: "crm", label: "CRM" },
  { key: "clienti", label: "Clienti" },
  { key: "comenzi", label: "Comenzi vanzare" },
];

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const COLUMNS = [
  ["agent", "Agent"],
  ["nume", "Nume"],
  ["firma", "Firma"],
  ["tel", "Tel"],
  ["mail", "Mail"],
  ["valoare_fmt", "Valoare oferta"],
  ["data_oferta", "Data oferta"],
  ["comandata", "Comandata"],
];

const CUMULAT_COLUMNS = [
  ["entitate", "Entitate"],
  ["nume", "Nume / Firma"],
  ["tel", "Tel"],
  ["mail", "Mail"],
  ["agent", "Agent"],
  ["nr_oferte", "Nr oferte"],
  ["nr_comandate", "Nr comandate"],
  ["valoare_fmt", "Valoare oferte"],
];

const VARIANTS = {
  erp: {
    layout: "Descarcari",
    prefix: "/api/descarcari",
    files: ["tabel_oferte", "cumulat_dealer_erp"],
    description: "Oferte din ERP (app.amel-online.ro), citite read-only. Filtreaza dupa sursa si dupa data adaugarii ofertei. Export in Excel.",
  },
  vechi: {
    layout: "ERP VECHI",
    prefix: "/api/erp-vechi",
    files: ["tabel_oferte_erp_vechi", "cumulat_dealer_erp_vechi"],
    description: "Oferte din ERP vechi (arhiva.amel-online.ro), citite read-only. Ofertele dezactivate sunt excluse. Filtreaza dupa sursa si dupa data adaugarii ofertei. Export in Excel.",
  },
};

const buildViews = (v) => ({
  oferte: { label: "Oferte", endpoint: `${v.prefix}/tabel-oferte`, file: v.files[0], columns: COLUMNS, unit: "oferte" },
  cumulat: { label: "Cumulat dealer", endpoint: `${v.prefix}/cumulat-dealer`, file: v.files[1], columns: CUMULAT_COLUMNS, unit: "entitati" },
});

const NUMERIC_SORT = { valoare_fmt: "valoare", nr_oferte: "nr_oferte", nr_comandate: "nr_comandate" };

export default function Descarcari({ variant = "erp" }) {
  const V = VARIANTS[variant];
  const VIEWS = useMemo(() => buildViews(V), [V]);
  const [selected, setSelected] = useState({ crm: true, clienti: true, comenzi: true });
  const [dateStart, setDateStart] = useState(firstOfMonth());
  const [dateEnd, setDateEnd] = useState(today());
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [view, setView] = useState("oferte");
  const [total, setTotal] = useState(null);
  const cfg = VIEWS[view];
  const COLS = cfg.columns;

  const switchView = (v) => {
    setView(v);
    setRows(null);
    setMeta(null);
    setTotal(null);
    setSort({ key: null, dir: "asc" });
  };

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));

  const onSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortedRows = useMemo(() => {
    if (!rows || !sort.key) return rows;
    const numeric = NUMERIC_SORT[sort.key];
    const arr = [...rows];
    arr.sort((a, b) => {
      let av;
      let bv;
      if (numeric) {
        av = Number(a[numeric]) || 0;
        bv = Number(b[numeric]) || 0;
      } else {
        av = (a[sort.key] ?? "").toString().toLowerCase();
        bv = (b[sort.key] ?? "").toString().toLowerCase();
      }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  const buildParams = useCallback(() => {
    const chosen = SOURCES.filter((s) => selected[s.key]).map((s) => s.key);
    const allSelected = chosen.length === SOURCES.length || chosen.length === 0;
    const params = {};
    params.sursa = allSelected ? "toate" : chosen.join(",");
    if (dateStart) params.date_start = dateStart;
    if (dateEnd) params.date_end = dateEnd;
    return params;
  }, [selected, dateStart, dateEnd]);

  const load = useCallback(async () => {
    const chosen = SOURCES.filter((s) => selected[s.key]);
    if (chosen.length === 0) {
      toast.error("Selecteaza cel putin o sursa.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(cfg.endpoint, { params: buildParams() });
      setRows(data.rows);
      setTotal(data.total || null);
      setMeta({ count: data.count, truncated: data.truncated, limit: data.limit });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nu am putut incarca tabelul");
    } finally {
      setLoading(false);
    }
  }, [buildParams, selected, cfg]);

  const onExport = useCallback(async () => {
    const chosen = SOURCES.filter((s) => selected[s.key]);
    if (chosen.length === 0) {
      toast.error("Selecteaza cel putin o sursa.");
      return;
    }
    setExporting(true);
    try {
      const resp = await api.get(`${cfg.endpoint}/export`, {
        params: buildParams(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `${cfg.file}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel descarcat");
    } catch (err) {
      toast.error("Export esuat");
    } finally {
      setExporting(false);
    }
  }, [buildParams, selected, cfg]);

  return (
    <Layout title={V.layout}>
      <section className="mb-6">
        <p className="text-xs uppercase tracking-widest text-slate-400">Rapoarte</p>
        <h2 className="text-2xl font-semibold">Tabel Oferte</h2>
        <p data-testid="descarcari-description" className="mt-1 max-w-2xl text-sm text-slate-500">{V.description}</p>
      </section>

      <div data-testid="descarcari-view-tabs" className="mb-4 inline-flex rounded-md bg-slate-100 p-1 text-sm">
        {Object.entries(VIEWS).map(([v, c]) => (
          <button key={v} type="button" data-testid={`descarcari-view-${v}`} onClick={() => switchView(v)}
            className={`rounded px-3 py-1 ${view === v ? "bg-white font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{c.label}</button>
        ))}
      </div>

      <section
        data-testid="descarcari-filters"
        className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
      >
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <div className="mb-2 text-xs uppercase tracking-widest text-slate-400">Sursa</div>
            <div className="flex flex-wrap gap-2">
              {SOURCES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`source-toggle-${s.key}`}
                  onClick={() => toggle(s.key)}
                  className={`rounded border px-3 py-1.5 text-sm transition ${
                    selected[s.key]
                      ? "border-accent-500 bg-accent-500/10 text-accent-500"
                      : "border-slate-200 bg-slate-100 text-slate-500 hover:border-slate-400"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400" htmlFor="date-start">
              De la (data oferta)
            </label>
            <input
              id="date-start"
              data-testid="filter-date-start"
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="rounded border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm outline-none focus:border-accent-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400" htmlFor="date-end">
              Pana la
            </label>
            <input
              id="date-end"
              data-testid="filter-date-end"
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="rounded border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm outline-none focus:border-accent-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              data-testid="generate-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded bg-accent-500 px-4 py-2 text-sm font-semibold text-ink-950 hover:bg-accent-600 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />} Genereaza
            </button>
            <button
              type="button"
              data-testid="export-excel-btn"
              onClick={onExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60"
            >
              {exporting ? <Loader2 className="animate-spin" size={15} /> : <FileSpreadsheet size={15} />} Export Excel
            </button>
          </div>
        </div>
      </section>

      {meta ? (
        <div data-testid="result-meta" className="mb-3 flex items-center gap-3 text-sm text-slate-500">
          <Download size={14} className="text-accent-500" />
          <span data-testid="result-count">{meta.count} {cfg.unit}</span>
          {view === "cumulat" ? <span>(cumulat pe client / contact CRM)</span> : null}
          {meta.truncated ? (
            <span className="text-amber-700">
              (afisate primele {meta.limit}; restrange perioada pentru toate)
            </span>
          ) : null}
        </div>
      ) : null}

      <section className="overflow-x-auto rounded-lg border border-slate-200">
        <table data-testid="tabel-oferte" className="min-w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              {COLS.map(([key, label]) => (
                <th
                  key={key}
                  data-testid={`sort-${key}`}
                  onClick={() => onSort(key)}
                  className="cursor-pointer select-none px-3 py-2 font-medium hover:text-accent-600"
                >
                  <span className="inline-flex items-center gap-1">
                    {label}
                    {sort.key === key ? (
                      sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                    ) : (
                      <ChevronsUpDown size={12} className="opacity-40" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows == null ? (
              <tr>
                <td colSpan={COLS.length} className="px-3 py-8 text-center text-slate-400">
                  Seteaza filtrele si apasa Genereaza.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLS.length} className="px-3 py-8 text-center text-slate-400">
                  Nicio oferta pentru filtrele alese.
                </td>
              </tr>
            ) : (
              sortedRows.map((row, i) => (
                <tr
                  key={i}
                  data-testid={`tabel-row-${i}`}
                  className="border-t border-slate-100 odd:bg-white even:bg-slate-50"
                >
                  {COLS.map(([key]) => (
                    <td key={key} className="px-3 py-2 align-top">
                      {key === "comandata" ? (
                        <span
                          className={
                            row[key] === "Da"
                              ? "rounded bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700"
                              : "rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500"
                          }
                        >
                          {row[key]}
                        </span>
                      ) : (
                        <span className="text-slate-700">{row[key]}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
            {rows && rows.length > 0 && total ? (
              <tr data-testid="tabel-row-total" className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
                {COLS.map(([key]) => (
                  <td key={key} className="px-3 py-2">{total[key]}</td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}
